// Session handling on the client.
//
// The token arrives in the URL fragment from the magic link, gets stashed,
// and the fragment is stripped immediately so it does not sit in the address
// bar or get shared when someone copies the URL.
import { useCallback, useEffect, useState } from 'react';

const TOKEN_KEY = 'gmh.session';

// You should never be asked to sign in again unless you asked to sign out.
// Two things make that true.
//
// One: the token is mirrored into IndexedDB as well as localStorage. iOS can
// evict localStorage for a PWA under storage pressure, and losing the token
// to a housekeeping sweep would look identical to being logged out.
//
// Two: the worker slides the expiry, so opening the app at all extends it.
const saveToken = (t: string) => {
  try {
    localStorage.setItem(TOKEN_KEY, t);
  } catch {
    /* private mode, or full. IndexedDB below is the real store. */
  }
  void idbPut(TOKEN_KEY, t);
};

const clearToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* nothing to do */
  }
  void idbPut(TOKEN_KEY, null);
};

function idb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open('gmh-auth', 1);
    req.onupgradeneeded = () => req.result.createObjectStore('kv');
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(key: string, value: string | null): Promise<void> {
  try {
    const db = await idb();
    const t = db.transaction('kv', 'readwrite');
    if (value === null) t.objectStore('kv').delete(key);
    else t.objectStore('kv').put(value, key);
  } catch {
    /* best effort */
  }
}

async function idbGet(key: string): Promise<string | null> {
  try {
    const db = await idb();
    return await new Promise((resolve) => {
      const r = db.transaction('kv', 'readonly').objectStore('kv').get(key);
      r.onsuccess = () => resolve((r.result as string | undefined) ?? null);
      r.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

/** Reduces the chance the browser evicts our storage at all. */
function askForPersistence() {
  void navigator.storage?.persist?.().catch(() => undefined);
}

const readToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export interface Me {
  id: string;
  email: string;
  initials: string;
  isAdmin: boolean;
}

export interface ServerPlace {
  placeKey: string;
  label: string;
  address?: string;
  lat?: number;
  lon?: number;
  isHome: boolean;
  isDefaultDestination: boolean;
  modelling: 'richmond' | 'generic';
  pinned: boolean;
  updatedAt: string;
}

export type AuthState = 'loading' | 'anonymous' | 'signed-in' | 'single-user';

export function useAuth(workerUrl: string) {
  const [state, setState] = useState<AuthState>('loading');
  const [me, setMe] = useState<Me | null>(null);
  const [places, setPlaces] = useState<ServerPlace[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Pick up a token from the magic link, then clean the URL. If localStorage
  // has been wiped, recover from the IndexedDB mirror before concluding that
  // anybody is logged out.
  useEffect(() => {
    void (async () => {
      askForPersistence();
      const hash = new URLSearchParams(location.hash.replace(/^#/, ''));
      const fromLink = hash.get('token');
      if (fromLink) {
        saveToken(fromLink);
        history.replaceState(null, '', location.pathname + location.search);
        setToken(fromLink);
        return;
      }
      const existing = readToken() ?? (await idbGet(TOKEN_KEY));
      // Put it back where the fast path can find it next time.
      if (existing) saveToken(existing);
      setToken(existing);
      if (!existing) setState('anonymous');
    })();
  }, []);

  const load = useCallback(async () => {
    const t = readToken() ?? (await idbGet(TOKEN_KEY));
    if (!t) {
      setState('anonymous');
      return;
    }
    try {
      const res = await fetch(`${workerUrl}/auth/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });

      // The worker hands back a fresh token once the current one is past
      // halfway through its year, so an active user never expires.
      const renewed = res.headers.get('X-Session-Renewed');
      if (renewed) saveToken(renewed);

      // No D1 bound means accounts are not provisioned, and the app runs as
      // the single-user tool the spec originally described. Not an error.
      if (res.status === 404) {
        setState('single-user');
        return;
      }

      // Only a definitive rejection clears the token. A 500 or a 503 is the
      // worker having a bad day, not us being signed out, and wiping the
      // token on one would mean a deploy blip logs everybody out.
      if (res.status === 401 || res.status === 403) {
        clearToken();
        setToken(null);
        setMe(null);
        setState('anonymous');
        return;
      }

      if (!res.ok) {
        // Keep whatever we had and carry on.
        setState(me ? 'signed-in' : 'loading');
        return;
      }

      const body = (await res.json()) as { user: Me; places: ServerPlace[] };
      setMe(body.user);
      setPlaces(body.places ?? []);
      setState('signed-in');
    } catch (e) {
      // Offline is the normal case for this app (spec 7.2), so a failed
      // request must not sign anyone out. But claiming 'signed-in' when we
      // have never successfully loaded a user produces an account sheet that
      // says "Not signed in" beside a Sign out button, which is worse than an
      // honest error: it hides a real fault behind a plausible-looking UI.
      if (me) {
        setState('signed-in'); // we know who this is, just cannot reach the net
      } else {
        setError(e instanceof Error ? e.message : 'cannot reach the server');
        setState('anonymous');
      }
    }
  }, [workerUrl, me]);

  useEffect(() => {
    void load();
  }, [load, token]);

  const requestLink = useCallback(
    async (email: string): Promise<string> => {
      const res = await fetch(`${workerUrl}/auth/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
      return body.message ?? body.error ?? 'Something went wrong.';
    },
    [workerUrl]
  );

  // The only path that ends a session. Nothing else clears the token.
  const signOut = useCallback(() => {
    clearToken();
    // The cached /home payload is derived from this user's saved places, so
    // it goes with them rather than greeting whoever signs in next.
    void caches?.keys?.().then((ks) =>
      Promise.all(ks.filter((k) => k.startsWith('last-home')).map((k) => caches.delete(k)))
    ).catch(() => undefined);
    setToken(null);
    setMe(null);
    setPlaces([]);
    setState('anonymous');
  }, []);

  const authFetch = useCallback(
    async (path: string, init: RequestInit = {}) => {
      const t = readToken() ?? (await idbGet(TOKEN_KEY));
      const res = await fetch(`${workerUrl}${path}`, {
        ...init,
        headers: {
          ...(init.headers ?? {}),
          ...(t ? { Authorization: `Bearer ${t}` } : {}),
        },
      });
      const renewed = res.headers.get('X-Session-Renewed');
      if (renewed) saveToken(renewed);
      return res;
    },
    [workerUrl]
  );

  const savePlace = useCallback(
    async (place: Partial<ServerPlace> & { placeKey: string }) => {
      const res = await authFetch('/me/places', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ places: [place] }),
      });
      if (res.ok) {
        const body = (await res.json()) as { places: ServerPlace[] };
        setPlaces(body.places);
      }
    },
    [authFetch]
  );

  return { state, me, places, error, requestLink, signOut, authFetch, savePlace, reload: load };
}
